import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Rider } from '../models/rider.model';
import { environment } from 'src/environments/environment';
import { catchError, map, Observable, of, take } from 'rxjs';
import { Apollo } from 'apollo-angular';
import {
  CREATE_RIDER,
  GET_ALL_RIDERS,
  GET_RIDER_BY_ID,
} from 'src/app/shared/queries/rider-query';
import { GeoPoint } from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root',
})
export class RiderService {
  constructor(private firestore: AngularFirestore, public apollo: Apollo) {}

  getRiderByContact(contact: string): Observable<Rider | undefined> {
    return this.firestore
      .collection<Rider>(environment.RIDERS, (ref) =>
        ref.where('primaryContact', '==', contact)
      )
      .valueChanges({ idField: 'id' }) 
      .pipe(
        take(1),  
        map((riders) => (riders.length ? riders[0] : undefined)),
        catchError((error) => {
          console.error('Error fetching rider:', error);
          return of(undefined);  
        })
      );
  }

  updateRiderStatus(riderId: string, status: string | undefined) {
    this.firestore
      .collection('Riders', (ref) => ref.where('riderId', '==', riderId))
      .get()
      .subscribe((snapshot) => {
        if (!snapshot.empty) {
          snapshot.forEach((doc) => {
            this.firestore
              .collection('Riders')
              .doc(doc.id)
              .update({ status: status })
              .then(() => {
                console.log('Status updated successfully!');
              })
              .catch((error) => {
                console.error('Error updating status: ', error);
              });
          });
        } else {
          console.error('Rider not found!');
        }
      });
  }

  updateRiderLocation(riderId: string, latitude: number, longitude: number) {
    this.firestore
      .collection(environment.RIDERS, (ref) =>
        ref.where('riderId', '==', riderId)
      )
      .get()
      .subscribe((snapshot) => {
        if (!snapshot.empty) {
          snapshot.forEach((doc) => {
            this.firestore
              .collection('Riders')
              .doc(doc.id)
              .update({
                geoLocation: new GeoPoint(latitude, longitude),
              })
              .then(() => {
                console.log('Location updated successfully!');
              })
              .catch((error) => {
                console.error('Error updating location: ', error);
              });
          });
        } else {
          console.error('Rider not found!');
        }
      });
  }

  getAllRiders() {
    return this.apollo.watchQuery<any>({
      query: GET_ALL_RIDERS,
      fetchPolicy: 'network-only',
    }).valueChanges.pipe(
      map(result => result.data.GetAllRiders),
      catchError(error => {
        console.error('Error fetching riders:', error);
        return of([]); 
      })
    );
  }

  getRiderByPrimaryContact(primaryContact: string): Observable<Rider | undefined> {
    return this.getAllRiders().pipe(
      map(riders => riders.find((rider: { primaryContact: string; }) => rider.primaryContact === primaryContact))
    );
  }

  getRiderById(id: string): Observable<any> {
    return this.apollo
      .query({
        query: GET_RIDER_BY_ID,
        variables: { getRiderByIdId: id },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result: any) => result.data.GetRiderById));
  }

  async createRiderInFirebase(riderFromBackend: any) {
    const address = riderFromBackend.address || {};
    const rider = {
      name: riderFromBackend.name,
      primaryContact: riderFromBackend.primaryContact,
      city: address.city || '',
      email: riderFromBackend.email || '',
      riderId: riderFromBackend.id,
      state: address.state || '',
      geoLocation: new GeoPoint(12.99539, 77.69644), // Adding default location - Phoenix market city
      status: 'AVAILABLE',
      zone: '',
    };
    try {
      await this.firestore.collection(environment.RIDERS).add(rider);
      console.log('Rider successfully created!', rider);
    } catch (error) {
      console.log('Error creating rider', error);
      window.alert('Error logging in.');
    }
  }

  createRiderInBackend(rider: any): Observable<any> {
    return this.apollo.mutate({
      mutation: CREATE_RIDER,
      variables: { rider },
    });
  }
}
